import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { CMMeetingSummary } from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES, POPUP_COLORS } from '../../constants/popupStyles';

interface CMSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    meetingId?: number;
    token: string;
    onSuccess?: () => void;
    onStatusMessage?: (message: { type: 'success' | 'error' | 'loading'; text: string } | null) => void;
}

export const CMSummaryModal: React.FC<CMSummaryModalProps> = ({
    isOpen,
    onClose,
    meetingId,
    token,
    onSuccess,
    onStatusMessage,
}) => {
    const [summary, setSummary] = useState<CMMeetingSummary | null>(null);
    const [summaryText, setSummaryText] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [originalSummaryText, setOriginalSummaryText] = useState<string>('');

    // Load meeting summary when modal opens
    useEffect(() => {
        const loadMeetingSummary = async () => {
            if (!isOpen || !meetingId || !token) return;

            setIsLoading(true);
            onStatusMessage?.({ type: 'loading', text: 'Loading CM summary...' });

            try {
                const data = await apiService.getCMMeetingSummary({ token, meeting_id: meetingId });
                setSummary(data);
                setSummaryText(data.summary_text || '');
                setOriginalSummaryText(data.summary_text || '');
                onStatusMessage?.(null);
            } catch (error: any) {
                console.error('Failed to load CM summary:', error);
                onStatusMessage?.({
                    type: 'error',
                    text: error.message || 'Failed to load CM summary'
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadMeetingSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, meetingId, token]);

    // Check if data has changed
    const hasChanged = (): boolean => {
        return summaryText !== originalSummaryText;
    };

    // Handle save
    const handleSave = async () => {
        if (!meetingId || !token) return;

        setIsSaving(true);
        onStatusMessage?.({ type: 'loading', text: 'Saving summary...' });

        try {
            await apiService.updateCMMeetingSummary({
                token,
                meeting_id: meetingId,
                summary: summaryText,
            });

            onStatusMessage?.({ type: 'success', text: 'Summary saved successfully' });
            setOriginalSummaryText(summaryText);
            onSuccess?.();
        } catch (error: any) {
            console.error('Failed to save summary:', error);
            onStatusMessage?.({
                type: 'error',
                text: error.message || 'Failed to save summary'
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle approve
    const handleApprove = async () => {
        if (!meetingId || !token) return;

        setIsSaving(true);
        onStatusMessage?.({ type: 'loading', text: 'Approving summary...' });

        try {
            await apiService.approveCMSummary({
                token,
                meeting_id: meetingId,
                summary: summaryText,
            });

            onStatusMessage?.({ type: 'success', text: 'Summary approved successfully' });
            // Update original summary to match current, so no warning is shown
            setOriginalSummaryText(summaryText);
            onSuccess?.();
            handleClose(true); // Force close without warning
        } catch (error: any) {
            console.error('Failed to approve summary:', error);
            onStatusMessage?.({
                type: 'error',
                text: error.message || 'Failed to approve summary'
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle close
    const handleClose = (force: boolean = false) => {
        if (!force && hasChanged()) {
            const confirmClose = window.confirm('You have unsaved changes. Are you sure you want to close?');
            if (!confirmClose) return;
        }

        setSummary(null);
        setSummaryText('');
        setOriginalSummaryText('');
        onClose();
    };

    // Format date
    const formatDate = (timestamp: number) => {
        if (!timestamp || timestamp === 0) return '';
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    };

    const title = summary
        ? summary.meetingtype_id === 1 ? 'Case Manager Intake Summary' : 'Case Manager FollowUp Summary'
        : 'CM Summary';

    return (
        <ModalWrapper modalId="CMSummaryModal"
            isOpen={isOpen}
            onClose={() => handleClose()}
            title={title}
            isLoading={isSaving}
            width="!max-w-4xl"
            className="max-h-[90vh] flex flex-col"
            compactHeader={true}
        >
            {/* Content Container */}
            <div className="flex-1 overflow-y-auto px-3 pt-1 pb-3 min-h-0">
                {/* Meeting Info */}
                {summary && (
                    <div className="space-y-0 text-sm text-[#366A75]">
                        <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                            <span>{formatDate(summary.start)}</span>
                        </div>
                        <div className="pl-6">{summary.name}</div>
                        <div className="pl-6">{summary.patient_tz}</div>
                        {summary.HM_ID && <div className="pl-6">{summary.HM_ID}</div>}
                    </div>
                )}

                {/* Content */}
                {isLoading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#366A75]"></div>
                    </div>
                ) : (
                    <div className="mb-1">
                        <textarea
                            value={summaryText}
                            onChange={(e) => setSummaryText(e.target.value)}
                            className={`${POPUP_STYLES.input} h-[500px] resize-none`}
                            dir="rtl"
                            placeholder="Enter summary text here..."
                            disabled={isSaving}
                            style={{
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '13px',
                                lineHeight: '1.5',
                                color: POPUP_COLORS.titleText
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-center gap-4 px-4 py-1 flex-shrink-0">
                <button
                    onClick={handleApprove}
                    disabled={isSaving || isLoading}
                    className={POPUP_STYLES.button}
                >
                    Approve
                </button>
                <button
                    onClick={handleSave}
                    disabled={isSaving || isLoading || !hasChanged()}
                    className={POPUP_STYLES.button}
                >
                    Save
                </button>
                <button
                    onClick={() => handleClose()}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
